/**
 * API / DB link status for admin web header (right side).
 * green = connected, yellow = checking/ready, red = offline ??with fix panel.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../utils/api';
import { Wrench, RefreshCw, X } from 'lucide-react';

export function useLinkStatus() {
  return useQuery({
    queryKey: ['link-status'],
    queryFn: async ({ signal }) => {
      let pingOk = false;
      let db = null;
      let networkError = null;

      try {
        const ping = await api.get('/ping', { signal, timeout: 10_000 });
        pingOk = !!(ping.data?.ok ?? ping.status === 200);
      } catch (e) {
        networkError = e?.message || 'Cannot reach API';
      }

      if (pingOk) {
        try {
          const res = await api.get('/health/db', {
            signal,
            timeout: 12_000,
            validateStatus: (s) => s === 200 || s === 503,
          });
          db = res.data || { db: res.status === 503 ? 'error' : 'ok' };
          if (res.status === 503 && !db.db) db.db = 'error';
        } catch (e) {
          db = { db: 'error', message: e?.message || 'DB health failed' };
        }
      }

      return { pingOk, db, networkError, checkedAt: Date.now() };
    },
    refetchInterval: 30_000,
    retry: 1,
    staleTime: 10_000,
  });
}

/** @returns {'green'|'yellow'|'red'} */
export function deriveLinkColor({ isPending, isFetching, isError, data, hasUser }) {
  if (isPending && !data) return 'yellow';
  if (isError || data?.networkError || data?.pingOk === false) return 'red';
  if (data?.db?.db === 'error') return 'red';
  if (isFetching && !data?.pingOk) return 'yellow';
  if (data?.pingOk && data?.db?.db === 'ok' && hasUser) return 'green';
  if (data?.pingOk && hasUser) return 'yellow'; // API up, DB still settling
  if (data?.pingOk) return 'yellow';
  return 'red';
}

const DOT = {
  green: 'bg-green-500',
  yellow: 'bg-amber-400',
  red: 'bg-red-500',
};

const LABEL = {
  green: 'API ready',
    yellow: 'Checking…',
  red: 'API down',
};

export default function LinkStatusBar({ user, compact = false, tone = 'light' }) {
  const q = useLinkStatus();
  const [fixOpen, setFixOpen] = useState(false);
  const color = useMemo(
    () =>
      deriveLinkColor({
        isPending: q.isPending,
        isFetching: q.isFetching,
        isError: q.isError,
        data: q.data,
        hasUser: !!user,
      }),
    [q.isPending, q.isFetching, q.isError, q.data, user]
  );

  const loginLabel = user?.name || user?.username || user?.email || 'Signed in';
  const role = user?.role || '';
  const onDark = tone === 'dark';

  return (
    <div className={`flex items-center gap-2 ${compact ? '' : 'min-w-0'}`}>
      <button
        type="button"
        onClick={() => {
          if (color === 'red') setFixOpen(true);
          else q.refetch();
        }}
        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs shrink-0 transition-colors ${
          onDark
            ? 'border border-white/20 bg-white/10 text-white/90 hover:bg-white/15'
            : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
        }`}
        title={LABEL[color]}
        aria-label={`Link status: ${LABEL[color]}`}
      >
        <span className={`inline-block h-2.5 w-2.5 rounded-full ${DOT[color]}`} aria-hidden />
        {!compact ? <span className="hidden sm:inline font-medium">{LABEL[color]}</span> : null}
      </button>

      <div className="min-w-0 text-right leading-tight">
        <p
          className={`text-sm font-semibold truncate max-w-[10rem] sm:max-w-[14rem] ${
            onDark ? 'text-white' : 'text-gray-900'
          }`}
        >
          {loginLabel}
        </p>
        {role ? (
          <p className={`text-[10px] truncate ${onDark ? 'text-white/60' : 'text-gray-500'}`}>{role}</p>
        ) : null}
      </div>

      {color === 'red' ? (
        <button
          type="button"
          onClick={() => setFixOpen(true)}
          className="inline-flex items-center gap-1 rounded-lg bg-red-500 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-red-600 shrink-0"
        >
          <Wrench className="w-3.5 h-3.5" />
          Check &amp; fix
        </button>
      ) : null}

      {fixOpen ? (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-4">
          <div className="bg-card rounded-xl shadow-xl w-full max-w-md max-h-[85vh] overflow-y-auto p-4">
            <div className="flex items-start justify-between gap-2 mb-3">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Connection fix</h2>
                <p className="text-xs text-gray-500 mt-0.5">Web ??API ??database</p>
              </div>
              <button type="button" onClick={() => setFixOpen(false)} className="p-1 text-gray-500" aria-label="Close">
                <X className="w-5 h-5" />
              </button>
            </div>

            <ol className="list-decimal list-inside text-sm text-gray-800 space-y-2 mb-4">
              <li>
                Start API: run <code className="text-xs bg-gray-100 px-1 rounded">START_BACKEND.bat</code> or{' '}
                <code className="text-xs bg-gray-100 px-1 rounded">npm run dev</code> in{' '}
                <code className="text-xs bg-gray-100 px-1 rounded">web/backend</code>.
              </li>
              <li>Confirm MySQL / XAMPP is running (DB must accept connections).</li>
              <li>
                Check <code className="text-xs bg-gray-100 px-1 rounded">web/backend/.env</code> ??DB_HOST, DB_PASSWORD,
                DB_NAME.
              </li>
              <li>Open the site from the Vite URL (same PC as the API), then retry below.</li>
              <li>Production: API must be up at admin.cbsl.co.nz (PM2 / Nginx).</li>
            </ol>

            {q.data?.networkError ? (
              <p className="text-xs font-mono text-red-700 bg-red-50 border border-red-100 rounded p-2 mb-3 break-all">
                {q.data.networkError}
              </p>
            ) : null}
            {q.data?.db?.db === 'error' ? (
              <p className="text-xs text-red-800 bg-red-50 border border-red-100 rounded p-2 mb-3">
                {q.data.db.hint || q.data.db.message || 'Database unreachable'}
              </p>
            ) : null}

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  q.refetch();
                }}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700"
              >
                <RefreshCw className={`w-4 h-4 ${q.isFetching ? 'animate-spin' : ''}`} />
                Retry link check
              </button>
              <Link
                to="/settings/db"
                onClick={() => setFixOpen(false)}
                className="inline-flex items-center px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-800 hover:bg-gray-50"
              >
                DB settings
              </Link>
              <button
                type="button"
                onClick={() => setFixOpen(false)}
                className="px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
